import { useState, useEffect, useRef } from 'react'
import { useAuth } from '../../contexts/AuthContext'
import { supabase } from '../../lib/supabase'
import {
  addToWaitlist,
  checkWaitlistStatus,
  getVehicles
} from '../../lib/supabase'
import { submitToBrevo } from '../../lib/brevo'
import { format, formatDistanceToNow } from 'date-fns'
import {
  Send,
  MessageCircle,
  AlertCircle,
  Check,
  Clock,
  Shield,
  Wrench,
  Car,
  Calendar,
  Phone,
  Star,
  ChevronDown,
  ChevronUp,
  Sparkles,
  Plus,
  Menu,
  X,
  Zap,
  MapPin,
  FileText,
  ClipboardList,
  User as UserIcon
} from 'lucide-react'
import {
  createChatSession,
  getChatSessions,
  getChatMessages,
  addChatMessage
} from '../../lib/workOrders'
import ConciergeRequests from '../../components/ConciergeRequests'

// ============================================
// AI CHAT COMPONENTS (for logged-in members)
// ============================================

const TypingIndicator = () => (
  <div className="flex items-center gap-1">
    <div className="w-2 h-2 bg-rdc-taupe rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
    <div className="w-2 h-2 bg-rdc-taupe rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
    <div className="w-2 h-2 bg-rdc-taupe rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
  </div>
)

const ProviderCard = ({ provider }) => (
  <div className="bg-gradient-to-br from-rdc-cream to-white rounded-lg p-4 border border-rdc-warm-gray mb-3 inline-block">
    <div className="font-semibold text-black mb-2">{provider.name}</div>
    {provider.rating && (
      <div className="flex items-center gap-1 mb-2">
        {[...Array(Math.round(provider.rating))].map((_, i) => (
          <Star key={i} className="w-4 h-4 fill-rdc-gold text-rdc-gold" />
        ))}
        <span className="text-xs text-rdc-taupe ml-1">{provider.rating}/5</span>
      </div>
    )}
    {provider.specialties && provider.specialties.length > 0 && (
      <div className="text-xs text-rdc-taupe mb-2">
        <div className="font-medium text-black mb-1">Specialties:</div>
        <div className="flex flex-wrap gap-1">
          {provider.specialties.map((spec, i) => (
            <span key={i} className="bg-rdc-primary/10 text-rdc-primary px-2 py-0.5 rounded">
              {spec}
            </span>
          ))}
        </div>
      </div>
    )}
    {provider.phone && (
      <div className="flex items-center gap-2 mt-3 pt-3 border-t border-rdc-warm-gray">
        <Phone className="w-4 h-4 text-rdc-primary" />
        <a href={`tel:${provider.phone}`} className="text-sm text-rdc-primary hover:underline">
          {provider.phone}
        </a>
      </div>
    )}
    {provider.website && (
      <div className="flex items-center gap-2 mt-2">
        <Zap className="w-4 h-4 text-rdc-gold" />
        <a href={provider.website} target="_blank" rel="noopener noreferrer" className="text-sm text-rdc-primary hover:underline">
          Visit Website
        </a>
      </div>
    )}
  </div>
)

const WorkOrderCard = ({ workOrder }) => (
  <div className="bg-gradient-to-br from-blue-50 to-white rounded-lg p-4 border border-blue-200 mb-3 inline-block">
    <div className="flex items-start justify-between mb-2">
      <div className="font-semibold text-black">{workOrder.title}</div>
      <span className={`text-xs px-2 py-1 rounded-full font-medium ${
        workOrder.status === 'completed' ? 'bg-green-100 text-green-700' :
        workOrder.status === 'in_progress' ? 'bg-blue-100 text-blue-700' :
        'bg-yellow-100 text-yellow-700'
      }`}>
        {workOrder.status}
      </span>
    </div>
    {workOrder.description && (
      <p className="text-sm text-rdc-taupe mb-2">{workOrder.description}</p>
    )}
    <div className="flex items-center gap-2 mt-3 pt-3 border-t border-blue-100 text-xs text-rdc-taupe">
      <FileText className="w-4 h-4" />
      <span>Work Order #{workOrder.id?.slice(0, 8).toUpperCase()}</span>
    </div>
  </div>
)

const CallStatusCard = ({ call }) => (
  <div className="bg-gradient-to-br from-green-50 to-white rounded-lg p-4 border border-green-200 mb-3 inline-block">
    <div className="flex items-start justify-between mb-2">
      <div className="font-semibold text-black">Call Initiated</div>
      <span className={`text-xs px-2 py-1 rounded-full font-medium ${
        call.status === 'completed' ? 'bg-green-100 text-green-700' :
        call.status === 'in_progress' ? 'bg-blue-100 text-blue-700' :
        'bg-yellow-100 text-yellow-700'
      }`}>
        {call.status}
      </span>
    </div>
    <div className="flex items-center gap-2 mb-2">
      <Phone className="w-4 h-4 text-rdc-primary" />
      <span className="text-sm font-medium text-black">{call.providerName}</span>
    </div>
    {call.duration && (
      <div className="text-xs text-rdc-taupe">Duration: {call.duration}</div>
    )}
  </div>
)

const ChatMessage = ({ message, isUser }) => (
  <div className={`flex ${isUser ? 'justify-end' : 'justify-start'} mb-4`}>
    <div
      className={`max-w-[70%] px-4 py-3 rounded-2xl ${
        isUser
          ? 'bg-rdc-primary text-white rounded-br-sm'
          : 'bg-white border border-rdc-warm-gray text-black rounded-bl-sm'
      }`}
    >
      <div className="text-sm leading-relaxed whitespace-pre-wrap">{message.content}</div>

      {!isUser && message.tool_calls && Object.keys(message.tool_calls).length > 0 && (
        <div className="mt-3 pt-3 border-t border-rdc-warm-gray">
          {message.tool_calls.providers && message.tool_calls.providers.map((provider, i) => (
            <ProviderCard key={i} provider={provider} />
          ))}
          {message.tool_calls.workOrder && (
            <WorkOrderCard workOrder={message.tool_calls.workOrder} />
          )}
          {message.tool_calls.call && (
            <CallStatusCard call={message.tool_calls.call} />
          )}
        </div>
      )}

      <div className={`text-xs mt-2 ${isUser ? 'text-white/70' : 'text-rdc-taupe'} text-right`}>
        {format(new Date(message.created_at), 'h:mm a')}
      </div>
    </div>
  </div>
)

const QuickActionButton = ({ icon: Icon, text, onClick }) => (
  <button
    onClick={onClick}
    className="flex items-center gap-2 px-4 py-2 text-sm bg-white border border-rdc-warm-gray rounded-full text-rdc-dark-gray hover:bg-rdc-cream transition-colors"
  >
    <Icon className="w-4 h-4" />
    {text}
  </button>
)

const ChatSessionItem = ({ session, isActive, onClick }) => {
  const vehicleLabel = session.vehicle ? `${session.vehicle.year} ${session.vehicle.make} ${session.vehicle.model}` : 'General'
  return (
    <button
      onClick={onClick}
      className={`w-full text-left px-4 py-3 rounded-lg transition-colors ${
        isActive
          ? 'bg-rdc-primary text-white'
          : 'hover:bg-rdc-cream text-rdc-dark-gray'
      }`}
    >
      <div className="text-sm font-medium truncate">{session.title || 'Untitled'}</div>
      <div className={`text-xs ${isActive ? 'text-white/70' : 'text-rdc-taupe'} truncate`}>
        {vehicleLabel}
      </div>
      <div className={`text-xs ${isActive ? 'text-white/50' : 'text-rdc-taupe'} mt-1`}>
        {formatDistanceToNow(new Date(session.updated_at), { addSuffix: true })}
      </div>
    </button>
  )
}

// ============================================
// LANDING PAGE COMPONENTS (for non-members)
// ============================================

const ServiceFeature = ({ icon: Icon, title, description }) => (
  <div className="flex gap-4 p-4">
    <div className="w-12 h-12 rounded-xl bg-rdc-primary/10 flex items-center justify-center flex-shrink-0">
      <Icon className="w-6 h-6 text-rdc-primary" />
    </div>
    <div>
      <h3 className="font-semibold text-black mb-1">{title}</h3>
      <p className="text-rdc-taupe text-sm">{description}</p>
    </div>
  </div>
)

const BenefitCard = ({ icon: Icon, title, items }) => (
  <div className="bg-white rounded-xl p-6 shadow-sm">
    <div className="w-12 h-12 rounded-xl bg-rdc-gold/20 flex items-center justify-center mb-4">
      <Icon className="w-6 h-6 text-rdc-gold" />
    </div>
    <h3 className="font-display text-lg font-semibold text-black mb-3">{title}</h3>
    <ul className="space-y-2">
      {items.map((item, idx) => (
        <li key={idx} className="flex items-start gap-2 text-sm text-rdc-taupe">
          <Check className="w-4 h-4 text-rdc-primary mt-0.5 flex-shrink-0" />
          <span>{item}</span>
        </li>
      ))}
    </ul>
  </div>
)

const TestimonialCard = ({ quote, author, vehicle }) => (
  <div className="bg-rdc-cream rounded-xl p-6">
    <div className="flex gap-1 mb-3">
      {[...Array(5)].map((_, i) => (
        <Star key={i} className="w-4 h-4 fill-rdc-gold text-rdc-gold" />
      ))}
    </div>
    <p className="text-rdc-dark-gray mb-4 italic">"{quote}"</p>
    <div>
      <p className="font-semibold text-black">{author}</p>
      <p className="text-sm text-rdc-taupe">{vehicle}</p>
    </div>
  </div>
)

const FAQItem = ({ question, answer, isOpen, onToggle }) => (
  <div className="border-b border-rdc-warm-gray/40 last:border-0">
    <button
      onClick={onToggle}
      className="w-full py-4 flex justify-between items-center text-left"
    >
      <span className="font-medium text-black">{question}</span>
      {isOpen ? (
        <ChevronUp className="w-5 h-5 text-rdc-taupe" />
      ) : (
        <ChevronDown className="w-5 h-5 text-rdc-taupe" />
      )}
    </button>
    {isOpen && (
      <div className="pb-4 text-rdc-taupe text-sm animate-fade-in">
        {answer}
      </div>
    )}
  </div>
)

// ============================================
// AI CONCIERGE CHAT (for logged-in members)
// ============================================

function AIConciergeChat() {
  const { user, profile } = useAuth()
  const messagesEndRef = useRef(null)
  const inputRef = useRef(null)

  // Chat state
  const [currentSession, setCurrentSession] = useState(null)
  const [sessions, setSessions] = useState([])
  const [messages, setMessages] = useState([])
  const [newMessage, setNewMessage] = useState('')
  const [selectedVehicle, setSelectedVehicle] = useState(null)
  const [vehicles, setVehicles] = useState([])
  const [sessionMode, setSessionMode] = useState('ai')

  // UI state
  const [loading, setLoading] = useState(true)
  const [sending, setSending] = useState(false)
  const [error, setError] = useState('')
  const [sidebarOpen, setSidebarOpen] = useState(true)
  const [isTyping, setIsTyping] = useState(false)

  const quickActions = [
    { icon: Wrench, text: 'Schedule Service' },
    { icon: FileText, text: 'Get a Quote' },
    { icon: MapPin, text: 'Arrange Transport' },
    { icon: Star, text: 'Find a Specialist' }
  ]

  // Load sessions and vehicles on mount
  useEffect(() => {
    loadData()
  }, [user])

  // Auto-scroll to bottom
  useEffect(() => {
    scrollToBottom()
  }, [messages, isTyping])

  // Poll for new messages every 10s (catches async call follow-ups)
  useEffect(() => {
    if (!currentSession?.id) return

    const interval = setInterval(async () => {
      if (sending || isTyping) return // Don't poll while actively chatting
      try {
        const { data: latestMsgs } = await getChatMessages(currentSession.id)
        if (latestMsgs && latestMsgs.length > messages.length) {
          setMessages(latestMsgs)
        }

        // Also poll for session mode changes
        const { data: sessionData } = await supabase
          .from('ai_chat_sessions')
          .select('mode')
          .eq('id', currentSession.id)
          .single()

        if (sessionData && sessionData.mode) {
          setSessionMode(sessionData.mode)
        }
      } catch (err) {
        // Silent fail on poll
      }
    }, 10000)

    return () => clearInterval(interval)
  }, [currentSession?.id])

  // Load chat data
  const loadData = async () => {
    if (!user?.id) return

    try {
      setLoading(true)

      // Load vehicles
      const { data: vehiclesData, error: vehiclesError } = await getVehicles(user.id)
      if (!vehiclesError && vehiclesData) {
        setVehicles(vehiclesData)
        if (vehiclesData.length > 0) {
          setSelectedVehicle(vehiclesData[0].id)
        }
      }

      // Load chat sessions
      const { data: sessionsData, error: sessionsError } = await getChatSessions(user.id, { limit: 20 })
      if (!sessionsError && sessionsData && sessionsData.length > 0) {
        setSessions(sessionsData)

        // Load the most recent session - pass session object directly
        const mostRecentSession = sessionsData[0]
        setCurrentSession(mostRecentSession)
        setSessionMode(mostRecentSession.mode || 'ai')
        const { data: msgs, error: msgsError } = await getChatMessages(mostRecentSession.id)
        if (!msgsError) {
          setMessages(msgs || [])
        }
      } else {
        // No sessions, create a new one
        await createNewSession()
      }
    } catch (err) {
      console.error('Error loading data:', err)
      setError('Failed to load chat')
    } finally {
      setLoading(false)
    }
  }

  const createNewSession = async () => {
    try {
      const { data: newSession, error: createError } = await createChatSession(
        user.id,
        selectedVehicle
      )

      if (createError) throw createError

      setCurrentSession(newSession)
      setMessages([])
      setSessions([newSession, ...sessions])

      // Add welcome message
      const welcomeMsg = {
        id: 'welcome',
        session_id: newSession.id,
        role: 'assistant',
        content: "Hello! I'm your RDC concierge AI assistant. I can help you find service providers, schedule maintenance, arrange transport, and manage your collection. What can I help you with today?",
        created_at: new Date().toISOString(),
        tool_calls: null
      }
      setMessages([welcomeMsg])
    } catch (err) {
      console.error('Error creating session:', err)
      setError('Failed to create new chat session')
    }
  }

  const loadSession = async (sessionId) => {
    try {
      const { data: msgs, error: msgsError } = await getChatMessages(sessionId)
      if (msgsError) throw msgsError

      // Find session from current state, or create a minimal session object
      const session = sessions.find(s => s.id === sessionId) || { id: sessionId }
      setCurrentSession(session)
      setSessionMode(session.mode || 'ai')
      setMessages(msgs || [])
    } catch (err) {
      console.error('Error loading session:', err)
      setError('Failed to load chat session')
    }
  }

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }

  const handleSend = async (text) => {
    const messageText = (text || newMessage).trim()
    if (!messageText || !currentSession || sending) return

    setNewMessage('')
    setSending(true)
    setError('')

    try {
      // Add user message to local state
      const userMsg = {
        id: `user-${Date.now()}`,
        session_id: currentSession.id,
        role: 'user',
        content: messageText,
        created_at: new Date().toISOString(),
        tool_calls: null
      }
      setMessages(prev => [...prev, userMsg])

      // Save user message to database (don't block on failure)
      addChatMessage(currentSession.id, 'user', messageText).catch(err =>
        console.warn('Failed to save user message:', err)
      )

      // Call the real AI concierge API
      setIsTyping(true)

      // Build the messages array for the API (last 20 messages for context)
      const contextMessages = [...messages, userMsg]
        .filter(m => m.role === 'user' || m.role === 'assistant')
        .slice(-20)
        .map(m => ({ role: m.role, content: m.content }))

      const response = await fetch('/api/concierge/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          messages: contextMessages,
          sessionId: currentSession.id,
          clientId: user.id,
          vehicleId: selectedVehicle?.id || null
        })
      })

      let data
      try {
        data = await response.json()
      } catch (parseErr) {
        const text = await response.text().catch(() => '')
        console.error('Non-JSON response from API:', response.status, text)
        throw new Error(`Server error (${response.status}). Please try again.`)
      }

      if (!response.ok || !data.success) {
        throw new Error(data.error || data.message || 'Failed to get AI response')
      }

      const aiResponse = {
        id: `ai-${Date.now()}`,
        session_id: currentSession.id,
        role: 'assistant',
        content: data.message,
        created_at: new Date().toISOString(),
        tool_calls: null
      }
      setMessages(prev => [...prev, aiResponse])
      setIsTyping(false)

      // AI response is already saved to database by the API endpoint — don't duplicate
    } catch (err) {
      console.error('Error sending message:', err)
      setError(err.message || 'Failed to send message')
      setIsTyping(false)
    } finally {
      setSending(false)
      inputRef.current?.focus()
    }
  }

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-10 w-10 border-4 border-rdc-primary border-t-transparent" />
      </div>
    )
  }

  return (
    <div className="h-full flex gap-4 bg-gray-50">
      {/* Sidebar */}
      <div className={`${
        sidebarOpen ? 'w-64' : 'w-0'
      } hidden md:flex flex-col bg-white rounded-xl border border-rdc-warm-gray overflow-hidden transition-all duration-300`}>
        {/* Sidebar Header */}
        <div className="p-4 border-b border-rdc-warm-gray/40 flex items-center justify-between">
          <h2 className="font-semibold text-black">Chat History</h2>
          <button
            onClick={() => setSidebarOpen(false)}
            className="p-1 hover:bg-rdc-cream rounded transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* New Chat Button */}
        <div className="p-4 border-b border-rdc-warm-gray/40">
          <button
            onClick={createNewSession}
            className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-rdc-primary text-white rounded-lg hover:bg-rdc-primary-dark transition-colors text-sm font-medium"
          >
            <Plus className="w-4 h-4" />
            New Chat
          </button>
        </div>

        {/* Chat Sessions List */}
        <div className="flex-1 overflow-y-auto p-4 space-y-2">
          {sessions.length === 0 ? (
            <div className="text-center py-8">
              <MessageCircle className="w-8 h-8 text-rdc-warm-gray mx-auto mb-2 opacity-50" />
              <p className="text-xs text-rdc-taupe">No chat history yet</p>
            </div>
          ) : (
            sessions.map(session => (
              <ChatSessionItem
                key={session.id}
                session={session}
                isActive={currentSession?.id === session.id}
                onClick={() => loadSession(session.id)}
              />
            ))
          )}
        </div>
      </div>

      {/* Main Chat Area */}
      <div className="flex-1 flex flex-col">
        {/* Header */}
        <div className="bg-white rounded-xl border border-rdc-warm-gray px-4 py-3 mb-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="md:hidden p-2 hover:bg-rdc-cream rounded transition-colors"
            >
              <Menu className="w-5 h-5" />
            </button>
            <p className="text-sm text-rdc-taupe">Your personal automotive assistant</p>
          </div>
          <button
            onClick={createNewSession}
            className="flex items-center gap-2 px-4 py-2 bg-rdc-primary text-white rounded-lg hover:bg-rdc-primary-dark transition-colors text-sm font-medium"
          >
            <Plus className="w-4 h-4" />
            New Chat
          </button>
        </div>

        {/* Messages Area */}
        <div className="flex-1 overflow-y-auto bg-white rounded-xl border border-rdc-warm-gray p-6 mb-4">
          {messages.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-center px-8">
              <div className="w-16 h-16 rounded-full bg-rdc-primary/10 flex items-center justify-center mb-4">
                <Sparkles className="w-8 h-8 text-rdc-primary" />
              </div>
              <h3 className="font-display text-lg font-semibold text-black mb-2">
                Welcome, {profile?.full_name?.split(' ')[0] || 'there'}!
              </h3>
              <p className="text-rdc-taupe max-w-md">
                I'm your AI concierge assistant. I can help you find service providers, schedule maintenance, arrange transport, and manage your collection. What can I help you with today?
              </p>
            </div>
          ) : (
            <>
              {messages.map((msg) => (
                <ChatMessage
                  key={msg.id}
                  message={msg}
                  isUser={msg.role === 'user'}
                />
              ))}
              {isTyping && (
                <div className="flex justify-start mb-4">
                  <div className="bg-white border border-rdc-warm-gray rounded-2xl rounded-bl-sm px-4 py-3">
                    <TypingIndicator />
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </>
          )}
        </div>

        {/* Input Area */}
        <div className="bg-white rounded-xl border border-rdc-warm-gray p-4">
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3 text-red-700 text-sm">
              <AlertCircle size={18} className="flex-shrink-0" />
              <span>{error}</span>
              <button
                onClick={() => setError('')}
                className="ml-auto text-xs underline hover:no-underline"
              >
                Dismiss
              </button>
            </div>
          )}

          {/* Vehicle Selector */}
          <div className="mb-4">
            <label className="text-xs font-medium text-rdc-taupe block mb-2">Vehicle (optional)</label>
            <select
              value={selectedVehicle || ''}
              onChange={(e) => setSelectedVehicle(e.target.value || null)}
              className="w-full px-3 py-2 bg-rdc-cream border border-rdc-warm-gray rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-rdc-primary focus:border-transparent"
            >
              <option value="">Select vehicle (optional)</option>
              {vehicles.map(v => (
                <option key={v.id} value={v.id}>
                  {v.year} {v.make} {v.model}
                </option>
              ))}
            </select>
          </div>

          {/* Message Input */}
          <div className="flex gap-3 mb-3">
            <textarea
              ref={inputRef}
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Type your message..."
              rows={1}
              disabled={sending}
              className="flex-1 px-4 py-3 bg-rdc-cream border border-rdc-warm-gray rounded-full resize-none focus:outline-none focus:ring-2 focus:ring-rdc-primary focus:border-transparent disabled:opacity-50"
              style={{ minHeight: '48px', maxHeight: '120px' }}
            />
            <button
              onClick={() => handleSend()}
              disabled={!newMessage.trim() || sending}
              className="w-12 h-12 rounded-full bg-rdc-primary text-white flex items-center justify-center hover:bg-rdc-primary-dark transition-colors disabled:opacity-50 flex-shrink-0"
            >
              {sending ? (
                <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" />
              ) : (
                <Send size={20} />
              )}
            </button>
          </div>

          {/* Quick Actions */}
          {messages.length === 0 && (
            <div className="space-y-2">
              <p className="text-xs text-rdc-taupe font-medium">Quick actions:</p>
              <div className="flex flex-wrap gap-2">
                {quickActions.map((action, i) => (
                  <QuickActionButton
                    key={i}
                    icon={action.icon}
                    text={action.text}
                    onClick={() => handleSend(action.text)}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

// ============================================
// CONCIERGE LANDING PAGE (for non-members)
// ============================================

function ConciergeLanding() {
  const { user, profile } = useAuth()
  const [formData, setFormData] = useState({
    email: profile?.email || user?.email || '',
    phone: '',
    interest: 'full-service'
  })
  const [submitting, setSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [error, setError] = useState('')
  const [openFaq, setOpenFaq] = useState(null)
  const [alreadyOnWaitlist, setAlreadyOnWaitlist] = useState(false)

  // Check if already on waitlist
  useEffect(() => {
    const checkExisting = async () => {
      if (user?.email) {
        const { data } = await checkWaitlistStatus(user.email, user.id)
        if (data) {
          setAlreadyOnWaitlist(true)
          setSubmitted(true)
        }
      }
    }
    checkExisting()
  }, [user])

  const features = [
    {
      icon: Car,
      title: 'Vehicle Preparation',
      description: 'Your car will be ready exactly when you need it - detailed, fueled, and waiting.'
    },
    {
      icon: Wrench,
      title: 'Maintenance Coordination',
      description: 'We schedule and oversee all service appointments so you never miss a beat.'
    },
    {
      icon: Calendar,
      title: 'Event Planning',
      description: 'From rallies to shows, we handle logistics so you can focus on driving.'
    },
    {
      icon: Shield,
      title: 'Storage Oversight',
      description: 'Regular checks, battery maintenance, and climate control monitoring.'
    },
    {
      icon: Phone,
      title: '24/7 Support',
      description: 'Direct line to your dedicated concierge for any need, any time.'
    },
    {
      icon: Clock,
      title: 'Pickup & Delivery',
      description: 'White-glove transport to and from your location or any destination.'
    }
  ]

  const benefits = [
    {
      icon: Clock,
      title: 'Save Time',
      items: [
        'No more scheduling appointments',
        'No waiting at service centers',
        'Instant response to requests',
        'Proactive maintenance reminders'
      ]
    },
    {
      icon: Shield,
      title: 'Peace of Mind',
      items: [
        'Expert care from specialists',
        'Comprehensive documentation',
        'Insurance-friendly records',
        'Secure storage protocols'
      ]
    },
    {
      icon: Star,
      title: 'Premium Experience',
      items: [
        'Dedicated personal concierge',
        'Priority service scheduling',
        'Exclusive member events',
        'VIP treatment everywhere'
      ]
    }
  ]

  const testimonials = [
    {
      quote: "The concierge service has transformed how I enjoy my collection. I just say when I want to drive, and everything is handled.",
      author: "James R.",
      vehicle: "1965 Shelby Cobra"
    },
    {
      quote: "Having someone who truly understands collector cars manage my maintenance is invaluable. They catch things I'd never notice.",
      author: "Patricia M.",
      vehicle: "1957 Mercedes 300SL"
    },
    {
      quote: "I used to spend weekends prepping for events. Now I show up, drive, and enjoy. That's what it should be about.",
      author: "Michael T.",
      vehicle: "1970 Plymouth Superbird"
    }
  ]

  const faqs = [
    {
      question: 'When will the concierge service be available?',
      answer: 'We are planning to open enrollment in late 2026. Join the waitlist to be among the first notified when spots become available.'
    },
    {
      question: 'What vehicles are eligible for concierge service?',
      answer: 'The concierge service is designed for collector and enthusiast vehicles. Whether you have a vintage classic, modern supercar, or beloved daily driver, we can accommodate your needs.'
    },
    {
      question: 'How much does the concierge service cost?',
      answer: 'Pricing will depend on the level of service and number of vehicles. Waitlist members will receive early access to pricing and exclusive founding member rates.'
    },
    {
      question: 'Do I need to store my car at your facility?',
      answer: 'No, the concierge service works with vehicles stored at our facility or at your home. We can pick up and return your vehicle as needed.'
    },
    {
      question: 'Can I use the service for just one car?',
      answer: 'Absolutely! Whether you have one treasured vehicle or an extensive collection, the concierge service is tailored to your specific needs.'
    }
  ]

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')
    setSubmitting(true)

    try {
      // Validate email
      if (!formData.email || !formData.email.includes('@')) {
        throw new Error('Please enter a valid email address')
      }

      // Add to Supabase waitlist
      const { data, error: dbError } = await addToWaitlist({
        email: formData.email,
        phone: formData.phone,
        fullName: profile?.full_name || null,
        interest: formData.interest,
        userId: user?.id || null,
        source: 'concierge-landing'
      })

      if (dbError) {
        // Check for duplicate
        if (dbError.code === '23505' || dbError.message?.includes('duplicate')) {
          setAlreadyOnWaitlist(true)
          setSubmitted(true)
          return
        }
        throw dbError
      }

      // Also submit to Brevo for email marketing (non-blocking)
      try {
        await submitToBrevo({
          email: formData.email,
          phone: formData.phone,
          fullName: profile?.full_name || null,
          interest: formData.interest
        })
      } catch (brevoError) {
        // Don't fail the signup if Brevo fails - data is already in Supabase
        console.error('Brevo sync failed:', brevoError)
      }

      setSubmitted(true)
    } catch (err) {
      console.error('Waitlist signup error:', err)
      if (err?.message?.includes('relation') && err?.message?.includes('does not exist')) {
        setError('Waitlist system is being set up. Please try again shortly.')
      } else if (err?.code === '42P01') {
        setError('Waitlist system is being set up. Please try again shortly.')
      } else {
        setError(err?.message || 'Something went wrong. Please try again.')
      }
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="animate-fade-in">
      {/* Hero Section */}
      <div className="relative bg-gradient-to-br from-rdc-primary to-rdc-primary-dark rounded-2xl p-8 md:p-12 mb-8 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-0 w-96 h-96 bg-white rounded-full -translate-y-1/2 translate-x-1/2" />
          <div className="absolute bottom-0 left-0 w-64 h-64 bg-white rounded-full translate-y-1/2 -translate-x-1/2" />
        </div>

        <div className="relative z-10 max-w-2xl">
          <div className="flex items-center gap-2 mb-4">
            <Sparkles className="w-6 h-6 text-rdc-gold" />
            <span className="text-white font-medium">Coming 2026</span>
          </div>

          <h1 className="font-display text-3xl md:text-4xl font-bold text-white mb-4">
            Your Personal Car Concierge
          </h1>

          <p className="text-white/90 text-lg mb-6">
            Experience effortless car ownership. Our dedicated concierge team handles everything
            from maintenance scheduling to event preparation, so you can focus on what matters
            most – enjoying your vehicle.
          </p>

          {!submitted ? (
            <div className="flex flex-wrap gap-4">
              <a
                href="#waitlist"
                className="px-6 py-3 bg-white text-rdc-primary font-semibold rounded-full hover:bg-rdc-cream transition-colors"
              >
                Join the Waitlist
              </a>
              <a
                href="#features"
                className="px-6 py-3 border-2 border-white text-white font-semibold rounded-full hover:bg-white/10 transition-colors"
              >
                Learn More
              </a>
            </div>
          ) : (
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/20 rounded-full text-white">
              <Check className="w-5 h-5" />
              <span>{alreadyOnWaitlist ? "You're on the waitlist!" : "Thanks for joining!"}</span>
            </div>
          )}
        </div>
      </div>

      {/* Features Section */}
      <section id="features" className="mb-12">
        <h2 className="font-display text-2xl font-bold text-black mb-6">
          What's Included
        </h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {features.map((feature, idx) => (
            <div key={idx} className="bg-white rounded-xl">
              <ServiceFeature {...feature} />
            </div>
          ))}
        </div>
      </section>

      {/* Benefits Section */}
      <section className="mb-12">
        <h2 className="font-display text-2xl font-bold text-black mb-6">
          Why Concierge?
        </h2>
        <div className="grid md:grid-cols-3 gap-6">
          {benefits.map((benefit, idx) => (
            <BenefitCard key={idx} {...benefit} />
          ))}
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="mb-12">
        <h2 className="font-display text-2xl font-bold text-black mb-6">
          What Members Say
        </h2>
        <div className="grid md:grid-cols-3 gap-6">
          {testimonials.map((testimonial, idx) => (
            <TestimonialCard key={idx} {...testimonial} />
          ))}
        </div>
      </section>

      {/* Waitlist Form Section */}
      <section id="waitlist" className="mb-12">
        <div className="bg-rdc-cream rounded-2xl p-8 md:p-10">
          <div className="max-w-xl mx-auto text-center">
            {submitted ? (
              <div className="animate-fade-in">
                <div className="w-16 h-16 rounded-full bg-rdc-primary/10 flex items-center justify-center mx-auto mb-4">
                  <Check className="w-8 h-8 text-rdc-primary" />
                </div>
                <h2 className="font-display text-2xl font-bold text-black mb-3">
                  {alreadyOnWaitlist ? "You're Already on the List!" : "You're on the Waitlist!"}
                </h2>
                <p className="text-rdc-taupe">
                  {alreadyOnWaitlist
                    ? "We have your information and will reach out as soon as enrollment opens."
                    : "Thank you for your interest in the Concierge service. We'll notify you as soon as enrollment opens."
                  }
                </p>
              </div>
            ) : (
              <>
                <h2 className="font-display text-2xl font-bold text-black mb-3">
                  Join the Waitlist
                </h2>
                <p className="text-rdc-taupe mb-6">
                  Be among the first to experience our concierge service when it launches.
                  Waitlist members receive priority enrollment and exclusive founding member rates.
                </p>

                {error && (
                  <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3 text-red-700 text-left">
                    <AlertCircle size={20} className="flex-shrink-0" />
                    <span>{error}</span>
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-4 text-left">
                  <div>
                    <label className="block text-sm font-medium text-black mb-1">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="w-full px-4 py-3 bg-white border border-rdc-warm-gray rounded-lg focus:outline-none focus:ring-2 focus:ring-rdc-primary focus:border-transparent"
                      placeholder="your@email.com"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-black mb-1">
                      Phone Number (optional)
                    </label>
                    <input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className="w-full px-4 py-3 bg-white border border-rdc-warm-gray rounded-lg focus:outline-none focus:ring-2 focus:ring-rdc-primary focus:border-transparent"
                      placeholder="(555) 555-5555"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-black mb-1">
                      What interests you most?
                    </label>
                    <select
                      value={formData.interest}
                      onChange={(e) => setFormData({ ...formData, interest: e.target.value })}
                      className="w-full px-4 py-3 bg-white border border-rdc-warm-gray rounded-lg focus:outline-none focus:ring-2 focus:ring-rdc-primary focus:border-transparent"
                    >
                      <option value="full-service">Full Concierge Service</option>
                      <option value="maintenance">Maintenance Coordination</option>
                      <option value="events">Event Preparation</option>
                      <option value="storage">Storage & Care</option>
                      <option value="transport">Pickup & Delivery</option>
                    </select>
                  </div>

                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full py-3 bg-rdc-primary text-white font-semibold rounded-lg hover:bg-rdc-primary-dark transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {submitting ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" />
                        Joining...
                      </>
                    ) : (
                      'Join Waitlist'
                    )}
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="mb-12">
        <h2 className="font-display text-2xl font-bold text-black mb-6">
          Frequently Asked Questions
        </h2>
        <div className="bg-white rounded-xl p-6">
          {faqs.map((faq, idx) => (
            <FAQItem
              key={idx}
              question={faq.question}
              answer={faq.answer}
              isOpen={openFaq === idx}
              onToggle={() => setOpenFaq(openFaq === idx ? null : idx)}
            />
          ))}
        </div>
      </section>
    </div>
  )
}

// ============================================
// MAIN COMPONENT
// ============================================

export default function Concierge() {
  const { user } = useAuth()
  const [activeTab, setActiveTab] = useState('chat')

  if (!user) return <ConciergeLanding />

  return (
    <div className="animate-fade-in h-[calc(100vh-8rem)] flex flex-col">
      {/* Tab header */}
      <div className="flex items-center gap-1 mb-4 bg-white rounded-xl border border-rdc-warm-gray p-1.5">
        <button
          onClick={() => setActiveTab('chat')}
          className={`flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium transition-colors ${
            activeTab === 'chat'
              ? 'bg-rdc-primary text-white'
              : 'text-rdc-taupe hover:bg-rdc-cream'
          }`}
        >
          <Sparkles size={16} />
          Concierge Chat
        </button>
        <button
          onClick={() => setActiveTab('requests')}
          className={`flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium transition-colors ${
            activeTab === 'requests'
              ? 'bg-rdc-primary text-white'
              : 'text-rdc-taupe hover:bg-rdc-cream'
          }`}
        >
          <ClipboardList size={16} />
          My Requests
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 min-h-0">
        {activeTab === 'chat' ? <AIConciergeChat /> : <ConciergeRequests />}
      </div>
    </div>
  )
}
